
import type { LabelConfig, LabelVariant, CustomBackground, CustomShape, VariantSettings, CustomTemplate } from '../types';
import { COLOR_THEMES, FONTS } from '../constants';
import { code128Widths, code128ModuleCount } from './code128';
// @ts-ignore
import QRCode from 'qrcode';

export interface LayoutItem {
  x: number;
  y: number;
  w: number;
  h: number;
  variant: string;
}

let measureCtx: CanvasRenderingContext2D | null = null;
function measureTextWidth(text: string, fontSizePx: number, fontFamily: string, bold: boolean, italic: boolean): number {
    if (!measureCtx) {
        measureCtx = document.createElement('canvas').getContext('2d');
    }
    if (!measureCtx) return 0;
    measureCtx.font = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${fontSizePx}px ${fontFamily}, Arial, sans-serif`;
    return measureCtx.measureText(text).width;
}

// Text wider than its sticker used to be cut off, losing the START of the name. Ordinary
// stickers now shrink the text to fit — as templates with a text zone already did — but never
// below TEXT_FIT_MIN_SCALE of the chosen size: smaller is unreadable, so beyond that the text is
// still clipped and the configuration panel warns about it (see getTextFit).
export const TEXT_FIT_MIN_SCALE = 0.5;
const TEXT_FIT_WIDTH_RECT = 0.92;    // share of the sticker width the widest line may use
const TEXT_FIT_WIDTH_CIRCLE = 0.8;   // narrower: the lower lines sit on the shorter chord of a circle

/** The text lines printed on one sticker (name and class, split according to the wrap settings). */
export function getLabelTextLines(config: LabelConfig, settings: VariantSettings): string[] {
    const activeName = (settings.customName !== undefined && settings.customName !== '') ? settings.customName : config.name;
    const activeClass = (settings.customClassName !== undefined && settings.customClassName !== '') ? settings.customClassName : config.className;

    if (!settings.wrapText) {
        return [activeClass ? `${activeName} ${activeClass}` : activeName];
    }
    if (settings.forceTextSplit) {
        const nameParts = activeName.includes(' ') ? splitNameIntoTwoLines(activeName) : [activeName, null];
        return [...nameParts, activeClass || null].filter(Boolean) as string[];
    }
    if (activeClass) {
        // Class filled: name stays together on line 1, class on line 2.
        return [activeName, activeClass];
    }
    // No class: split the name itself across the two lines.
    const nameParts = activeName.includes(' ') ? splitNameIntoTwoLines(activeName) : [activeName, null];
    return nameParts.filter(Boolean) as string[];
}

/**
 * A template (or uploaded full image) brings its own outline, so a `shapeId: 'circle'` left over
 * from the slot it was picked on (e.g. the admin's default "circle-sm") must not clip it to a circle.
 */
export function isCircleSticker(settings: VariantSettings): boolean {
    return settings.shapeId === 'circle' && !settings.isFullImage && !settings.templateId;
}

interface TextFit { fSize: number; widest: number; overflow: boolean; }

/** Shrinks `baseSize` so the widest line fits the sticker; `overflow` is true when even the minimum size is too big. */
function fitLabelText(lines: string[], baseSize: number, w: number, isCircle: boolean, fontFamily: string, bold: boolean, italic: boolean): TextFit {
    let widest = 0;
    lines.forEach(line => {
        if (!line) return;
        widest = Math.max(widest, measureTextWidth(line, baseSize, fontFamily, bold, italic));
    });
    const available = w * (isCircle ? TEXT_FIT_WIDTH_CIRCLE : TEXT_FIT_WIDTH_RECT);
    if (widest <= available || widest === 0) return { fSize: baseSize, widest, overflow: false };
    const wanted = available / widest;
    const scale = Math.max(wanted, TEXT_FIT_MIN_SCALE);
    return { fSize: baseSize * scale, widest: widest * scale, overflow: wanted < TEXT_FIT_MIN_SCALE };
}

const baseLabelFontSize = (w: number, h: number, settings: VariantSettings) =>
    (w > h * 1.5 ? h * 0.35 : h * 0.18) * (settings.fontScale || 1);

/**
 * For the configuration panel: does the text of this sticker get shrunk, and does it still not fit?
 * Returns null where there is nothing to judge (templates with a text zone shrink on their own,
 * text-less templates print no text).
 */
export function getTextFit(config: LabelConfig, variantId: string, w: number, h: number, customTemplates?: CustomTemplate[]): { shrunk: boolean; overflow: boolean } | null {
    const settings = config.variantSettings[variantId];
    if (!settings || typeof document === 'undefined') return null;
    const template = settings.templateId ? customTemplates?.find(t => t.id === settings.templateId) : undefined;
    if (template && (template.textZone || template.hasText === false)) return null;
    const lines = getLabelTextLines(config, settings).filter(Boolean);
    if (lines.length === 0) return null;
    const baseSize = baseLabelFontSize(w, h, settings);
    const fit = fitLabelText(lines, baseSize, w, isCircleSticker(settings), settings.fontFamily || config.fontFamily || 'Arial', settings.isBold !== false, !!settings.isItalic);
    return { shrunk: fit.fSize < baseSize - 1e-6, overflow: fit.overflow };
}

/**
 * Text is measured with a canvas, which only knows a web font once it has loaded — measured too early it
 * uses the fallback font and the fit is wrong. Call before generating the print file. Never blocks for
 * more than a few seconds (blocked font CDN, offline).
 */
export async function ensureFontsLoaded(config: LabelConfig): Promise<void> {
    if (typeof document === 'undefined' || !document.fonts) return;
    const families = new Set<string>();
    const sample = new Set<string>([config.name, config.className]);
    for (const s of Object.values(config.variantSettings)) {
        if (s.visible === false || (s.customCount ?? 0) <= 0) continue;
        families.add(s.fontFamily || config.fontFamily || 'Arial');
        if (s.customName) sample.add(s.customName);
        if (s.customClassName) sample.add(s.customClassName);
    }
    const text = [...sample].join(' ');
    const loads = [...families].flatMap(family => [400, 700].map(weight => document.fonts.load(`${weight} 16px "${family}"`, text).catch(() => [])));
    await Promise.race([
        Promise.all(loads).then(() => document.fonts.ready),
        new Promise(resolve => setTimeout(resolve, 4000)),
    ]);
}

function escapeXml(unsafe: string): string {
    if (!unsafe) return '';
    return unsafe.toString().replace(/[<>&'"]/g, (c) => {
        switch (c) {
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '&': return '&amp;';
            case '\'': return '&apos;';
            case '"': return '&quot;';
            default: return c;
        }
    });
}

function adjustColor(color: string, amount: number): string {
    const hex = color.replace('#', '');
    const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.substring(0, 2), 16);
    const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.substring(2, 4), 16);
    const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.substring(4, 6), 16);
    const newR = Math.max(0, Math.min(255, r + amount));
    const newG = Math.max(0, Math.min(255, g + amount));
    const newB = Math.max(0, Math.min(255, b + amount));
    const toHex = (c: number) => {
        const h = c.toString(16);
        return h.length === 1 ? '0' + h : h;
    };
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

function hexToRgbUnit(hex: string): {r: number, g: number, b: number} {
    let c = hex.replace('#', '');
    if (c.length === 3) {
        c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2];
    }
    const val = parseInt(c, 16);
    return {
        r: ((val >> 16) & 255) / 255,
        g: ((val >> 8) & 255) / 255,
        b: (val & 255) / 255
    };
}

export function splitNameIntoTwoLines(name: string): [string, string | null] {
    const words = name.trim().split(/\s+/);
    if (words.length <= 1) return [name, null];
    let bestSplitIndex = -1;
    let minDiff = Infinity;
    for (let i = 1; i < words.length; i++) {
        const line1 = words.slice(0, i).join(' ');
        const line2 = words.slice(i).join(' ');
        const diff = Math.abs(line1.length - line2.length);
        if (diff < minDiff) { minDiff = diff; bestSplitIndex = i; }
    }
    const line1 = words.slice(0, bestSplitIndex).join(' ');
    const line2 = words.slice(bestSplitIndex).join(' ');
    return [line1, line2];
}

export const A4_WIDTH = 210;
export const A4_HEIGHT = 297;
export const MARGIN = 7;
export const GAP = 2;

export function sanitizeRxRy(svgContent: string): string {
    // Handle negative rx/ry with double quotes, single quotes, or no quotes
    return svgContent
        .replace(/\b(r[xy])\s*=\s*(["']?)-[\d.]+\2/g, '$1="0"');
}

export function stripScripts(svg: string): string {
    return svg
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<script[^>]*\/>/gi, '')
        .replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
        .replace(/(xlink:href|href)\s*=\s*["']\s*javascript:[^"']*/gi, '$1=""');
}

function scopeSvgIds(body: string, suffix: string): string {
    // Scope IDs
    const idRegex = /id=["']([^"']+)["']/g;
    let match;
    const ids = new Set<string>();
    while ((match = idRegex.exec(body)) !== null) ids.add(match[1]);
    let newBody = body;
    ids.forEach(id => {
        const newId = `${id}_${suffix}`;
        const escapedId = id.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        newBody = newBody.replace(new RegExp(`id=(["'])${escapedId}(["'])`, 'g'), `id=$1${newId}$2`);
        newBody = newBody.replace(new RegExp(`url\\(#${escapedId}\\)`, 'g'), `url(#${newId})`);
        newBody = newBody.replace(new RegExp(`href=(["'])#${escapedId}(["'])`, 'g'), `href=$1${newId}$2`);
        newBody = newBody.replace(new RegExp(`xlink:href=(["'])#${escapedId}(["'])`, 'g'), `xlink:href=$1${newId}$2`);
    });

    // Scope CSS class names to prevent cross-sticker style leaking.
    // Extract class names from <style> blocks only (not from class= attributes yet),
    // capturing ALL selectors including comma-separated ones like ".cls-2, .cls-3 {"
    const classes = new Set<string>();
    const styleBlockRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let styleMatch;
    while ((styleMatch = styleBlockRegex.exec(newBody)) !== null) {
        const styleContent = styleMatch[1];
        const selectorRegex = /\.([\w-]+)/g;
        let selMatch;
        while ((selMatch = selectorRegex.exec(styleContent)) !== null) classes.add(selMatch[1]);
    }
    classes.forEach(cls => {
        const escapedCls = cls.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        const newCls = `${cls}_${suffix}`;
        // Rename in <style> definitions: handles both ".cls-1 {" and ".cls-2, .cls-3 {"
        newBody = newBody.replace(new RegExp(`\\.${escapedCls}(\\s*[{,])`, 'g'), `.${newCls}$1`);
        // Rename in class="" attributes (whole word match)
        newBody = newBody.replace(new RegExp(`(class=["'][^"']*?)\\b${escapedCls}\\b`, 'g'), `$1${newCls}`);
    });

    // Convert CSS clip-path rules to inline SVG attributes.
    // CSS clip-path: url(#id) can be unreliable in browsers when SVG is embedded in HTML
    // (the clip coordinate system may not resolve correctly). SVG clip-path="" attribute always works.
    // Collect all rules first to avoid lastIndex issues when newBody is mutated inside the loop.
    const cssClipRules: Array<{ cls: string; clipValue: string }> = [];
    const cssClipRuleRegex = /\.([\w-]+)\s*\{[^}]*?clip-path:\s*(url\(#[^)]+\))[^}]*?\}/g;
    let clipRuleMatch;
    while ((clipRuleMatch = cssClipRuleRegex.exec(newBody)) !== null) {
        cssClipRules.push({ cls: clipRuleMatch[1], clipValue: clipRuleMatch[2] });
    }
    cssClipRules.forEach(({ cls, clipValue }) => {
        const escapedCls = cls.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
        // Add clip-path as inline SVG attribute (reliable in all renderers including Google Drive)
        newBody = newBody.replace(
            new RegExp(`(<[a-zA-Z][^>]*?class=["'][^"']*\\b${escapedCls}\\b[^"']*["'][^>]*?)(\\s*/>|\\s*>)`, 'g'),
            (match, tagPart, close) => {
                if (/\bclip-path\s*=/.test(tagPart)) return match;
                return `${tagPart} clip-path="${clipValue}"${close}`;
            }
        );
        // Remove clip-path from the CSS rule so CSS cannot override the inline attribute later.
        // Google Drive's renderer applies CSS after initial SVG attribute render, overriding with broken behavior.
        newBody = newBody.replace(
            new RegExp(`(\\.${escapedCls}\\s*\\{[^}]*?)clip-path:\\s*[^;]+;?([^}]*\\})`, 'g'),
            '$1$2'
        );
    });

    return newBody;
}

const svgInfoCache = new Map<string, { body: string, viewBox: string }>();

const LARGE_SVG_THRESHOLD = 50000; // 50 KB — above this, use <image> instead of inline SVG body
const svgDataUriCache = new Map<string, string>();

function toSvgDataUri(svgString: string): string {
    if (svgString.startsWith('data:')) return svgString; // Already a data URI — don't double-encode
    const cached = svgDataUriCache.get(svgString);
    if (cached) return cached;
    // btoa requires Latin1; unescape(encodeURIComponent()) handles Unicode SVGs
    const uri = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgString)))}`;
    svgDataUriCache.set(svgString, uri);
    return uri;
}

/**
 * Extract inner body and viewBox from SVG string.
 */
export function getSvgInfo(svgString: string): { body: string, viewBox: string } {
    if (!svgString) return { body: '', viewBox: '0 0 24 24' };

    // The whole string is the key: two different SVGs (e.g. QR codes of two order IDs) can share their length and
    // their first 200 characters, and must not get each other's cached result.
    const cacheKey = svgString;
    const cached = svgInfoCache.get(cacheKey);
    if (cached) return cached;
    
    let raw = svgString.trim();
    if (raw.startsWith('data:image/svg+xml;base64,')) {
        try { raw = atob(raw.split(',')[1]); } catch (e) { return { body: '', viewBox: '0 0 24 24' }; }
    }
    
    raw = raw.replace(/<\?xml.*?\?>/gi, '')
             .replace(/<!DOCTYPE.*?>/gi, '')
             .replace(/<!--[\s\S]*?-->/g, '')
             .trim();

    const viewBoxMatch = raw.match(/viewBox=["']([^"']+)["']/i);
    const viewBox = viewBoxMatch ? viewBoxMatch[1] : '0 0 24 24';
    
    const startTagEnd = raw.indexOf('>');
    const endTagStart = raw.lastIndexOf('</svg>');
    
    let body = "";
    if (startTagEnd !== -1 && endTagStart !== -1 && endTagStart > startTagEnd) {
        body = raw.substring(startTagEnd + 1, endTagStart);
    } else {
        body = raw.replace(/<svg[^>]*>/i, '').replace(/<\/svg>/gi, '');
    }

    const result = { body: stripScripts(sanitizeRxRy(body.trim())), viewBox };
    svgInfoCache.set(cacheKey, result);
    return result;
}

// Keeps the icon's bounding box fully inside the sticker's own shape, no matter how far
// iconScale/iconOffsetX/iconOffsetY are pushed by the size slider, mouse wheel, or drag —
// otherwise the overflowing part is silently clipped by the cut-line clip-path and ends up
// missing from the printed sheet. Used both by the renderer and by the interactive controls
// in ConfigurationPanel so the UI itself can't produce an out-of-bounds icon.
export function clampIconTransform(
    scale: number,
    offsetX: number,
    offsetY: number,
    w: number,
    h: number,
    isCircle: boolean
): { scale: number; offsetX: number; offsetY: number; size: number; x: number; y: number } {
    const baseSize = Math.min(w, h) * 0.45;
    let size = baseSize * scale;
    let x = (w - size) / 2 + offsetX;
    let y = h * 0.1 + offsetY;

    if (isCircle) {
        const cx = w / 2, cy = h / 2, radius = w / 2;
        size = Math.min(size, radius * Math.SQRT2);
        const safeRadius = radius - (size * Math.SQRT2) / 2;
        let dx = (x + size / 2) - cx;
        let dy = (y + size / 2) - cy;
        const dist = Math.hypot(dx, dy);
        if (dist > safeRadius && dist > 0) {
            dx = (dx / dist) * safeRadius;
            dy = (dy / dist) * safeRadius;
        }
        x = cx + dx - size / 2;
        y = cy + dy - size / 2;
    } else {
        size = Math.min(size, w, h);
        x = Math.min(Math.max(x, 0), w - size);
        y = Math.min(Math.max(y, 0), h - size);
    }

    return { size, x, y, scale: size / baseSize, offsetX: x - (w - size) / 2, offsetY: y - h * 0.1 };
}

export function generateLabelSvg(
    config: LabelConfig, 
    variantId: string, 
    x: number, 
    y: number, 
    w: number, 
    h: number, 
    showCutLine: boolean = true, 
    customBackgrounds?: CustomBackground[], 
    customShapes?: CustomShape[], 
    seed?: string,
    isPreviewMode: boolean = false,
    customTemplates?: CustomTemplate[],
    bleedMm: number = 1,
    includeCutLineMarks: boolean = showCutLine
): string {
    const settings = config.variantSettings[variantId];
    if (!settings) return '';

    const finalX = x + (settings.stickerOffsetX || 0);
    const finalY = y + (settings.stickerOffsetY || 0);

    const stickerSeed = seed || Math.random().toString(36).substr(2, 7);
    const clipId = `clip_${stickerSeed}`;
    const outlineFilterId = `outline_filter_${stickerSeed}`;

    const customShape = customShapes?.find(s => s.id === settings.shapeId);
    let shapePath = '';
    let isCircle = isCircleSticker(settings);
    // A template is clipped only to its own art box: rounded corners would cut into artwork that reaches them.
    const cornerRadius = (settings.sharpCorners || settings.isFullImage || settings.templateId) ? 0 : 2;

    const BLEED = Math.max(0, bleedMm); // mm — content extends BLEED mm beyond cut line on each side (admin-configurable, 0 disables bleed)
    if (customShape) {
        shapePath = stripScripts(sanitizeRxRy(customShape.content));
    } else if (isCircle) {
        shapePath = `<circle cx="${finalX + w/2}" cy="${finalY + h/2}" r="${w/2}" />`;
    } else {
        const rx = cornerRadius;
        shapePath = `<rect x="${finalX}" y="${finalY}" width="${w}" height="${h}" rx="${rx}" ry="${rx}" />`;
    }

    // Bleed shape: same geometry expanded by BLEED mm on every side.
    // Used for clip path and background fill so content covers the bleed area.
    // Cut line always uses the original shapePath (= final sticker size after cutting).
    let bleedShapePath: string;
    if (customShape) {
        bleedShapePath = shapePath; // complex geometry — bleed skipped for custom shapes
    } else if (isCircle) {
        bleedShapePath = `<circle cx="${finalX + w/2}" cy="${finalY + h/2}" r="${w/2 + BLEED}" />`;
    } else {
        const rx = cornerRadius;
        bleedShapePath = `<rect x="${finalX - BLEED}" y="${finalY - BLEED}" width="${w + BLEED * 2}" height="${h + BLEED * 2}" rx="${rx}" ry="${rx}" />`;
    }

    // --- SHARED DEFINITIONS ---
    // Bleed is only meaningful when the cut line is visible (admin/print file):
    // otherwise users have no reference for where the real edge is, so content
    // bleeding 1mm past it just looks like blur/overflow and can hide icons
    // that are actually positioned outside the sticker. For normal users we
    // clip tightly to the real cut shape instead.
    const previewClipShapePath = showCutLine ? bleedShapePath : shapePath;
    let defsInner = `<clipPath id="${clipId}">${previewClipShapePath}</clipPath>`;
    
    // --- BACKGROUND LAYER ---
    let backgroundLayer = '';
    let templateCutOutline = '';
    
    // Determine effective background image
    let effectiveFullImage = settings.fullImageData;
    let effectiveTextZone: CustomTemplate['textZone'];
    let effectiveIconZone: CustomTemplate['iconZone'];
    let effectiveHasIcon = true;
    let effectiveHasText = true;
    if (settings.templateId && customTemplates) {
        const temp = customTemplates.find(t => t.id === settings.templateId);
        if (temp) {
            effectiveFullImage = temp.data;
            effectiveTextZone = temp.textZone;
            effectiveIconZone = temp.iconZone;
            effectiveHasIcon = temp.hasIcon !== false;
            effectiveHasText = temp.hasText !== false;
        }
    }

    if (effectiveFullImage) {
        if (includeCutLineMarks) {
            // Templates don't have a fixed shapeId-derived outline (they're just art dropped
            // onto whatever shape is chosen), so the cut line traces the image's own alpha
            // silhouette instead of a plain rect/circle. This has to be drawn unclipped and
            // on top (see templateCutOutline below) — inside the background layer it used to
            // sit *under* the fully opaque part of the same image and was invisible whenever
            // the template had no transparency (i.e. almost always).
            // This is only the approximate outline shown on screen. The cut line in the print PDF is created
            // on the server from the artwork itself, as the PDF's own Chromium draws it (see cutTrace.js) — this
            // group also tells the server where the artwork is, so it must keep its image.
            defsInner += `
                <filter id="${outlineFilterId}" x="-20%" y="-20%" width="140%" height="140%">
                    <feMorphology in="SourceAlpha" result="expanded" operator="dilate" radius="0.3" />
                    <feFlood flood-color="#FF00FF" result="magenta" />
                    <feComposite in="magenta" in2="expanded" operator="in" result="outline" />
                    <feComposite in="outline" in2="SourceAlpha" operator="out" />
                </filter>`;
            templateCutOutline = `<image xlink:href="${effectiveFullImage}" href="${effectiveFullImage}" x="${finalX}" y="${finalY}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid slice" filter="url(#${outlineFilterId})" />`;
        }
        // With cut lines (admin view / print file) the artwork is enlarged by the bleed and cut along its own
        // silhouette. Without them it is clipped tightly to the sticker box, so the enlarged artwork lost its
        // edges on every side (a round template showed flat sides) — there it is drawn at the sticker's size.
        const artBleed = showCutLine ? BLEED : 0;
        backgroundLayer = `<image xlink:href="${effectiveFullImage}" href="${effectiveFullImage}" x="${finalX - artBleed}" y="${finalY - artBleed}" width="${w + artBleed * 2}" height="${h + artBleed * 2}" preserveAspectRatio="xMidYMid slice" />`;
    } else if (settings.backgroundId === null) {
        // Explicitly no background — leave backgroundLayer empty (transparent)
    } else {
        // Standard background
        const activeThemeId = settings.themeId || config.themeId;
        const activeCustomColor = settings.customColor || config.customColor;
        const activeBackgroundId = settings.backgroundId !== undefined ? settings.backgroundId : config.backgroundId;

        const theme = COLOR_THEMES.find(t => t.id === activeThemeId);
        let bgColor = (activeThemeId === 'custom' && activeCustomColor) ? activeCustomColor : (theme ? theme.primaryHex : '#ffffff');
        let secondaryHex = adjustColor(bgColor, 150);
        let visualFill = bgColor;
        
        const patternId = `pat_${stickerSeed}`;
        const gradId = `grad_${stickerSeed}`;
        const filterId = `filt_${stickerSeed}`;

        const customBg = customBackgrounds?.find(bg => bg.id === activeBackgroundId);
        if (customBg) {
            const pri = hexToRgbUnit(bgColor);
            defsInner += `<filter id="${filterId}" color-interpolation-filters="sRGB"><feColorMatrix type="matrix" values="0 0 0 0 ${pri.r} 0 0 0 0 ${pri.g} 0 0 0 0 ${pri.b} -0.2126 -0.7152 -0.0722 1 0" /></filter>`;
            defsInner += `<pattern id="${patternId}" patternUnits="userSpaceOnUse" x="${finalX}" y="${finalY}" width="${w}" height="${h}"><rect width="${w}" height="${h}" fill="${settings.useWhiteBackground ? '#ffffff' : secondaryHex}" /><image xlink:href="${customBg.data}" href="${customBg.data}" x="0" y="0" width="${w}" height="${h}" preserveAspectRatio="xMidYMid slice" filter="url(#${filterId})" /></pattern>`;
            visualFill = `url(#${patternId})`;
        } else if (theme?.pattern) {
            const scoped = scopeSvgIds(theme.pattern, stickerSeed);
            defsInner += scoped.replace(/id=["']([^"']+)["']/, `id="${patternId}"`);
            visualFill = `url(#${patternId})`;
        }

        if (settings.enableShading) {
            defsInner += `<linearGradient id="${gradId}" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="${adjustColor(bgColor, 30)}" /><stop offset="100%" stop-color="${adjustColor(bgColor, -30)}" /></linearGradient>`;
            if (!customBg && !theme?.pattern) visualFill = `url(#${gradId})`;
        }
        
        backgroundLayer = `<g fill="${visualFill}">${bleedShapePath}</g>`;
    }

    // --- ICON LAYER ---
    let iconSvg = '';
    const icon = settings.customIcon || config.icon;
    
    // --- TEXT COLOR LOGIC ---
    // Full-image backgrounds (templates / custom uploaded art) are usually light,
    // so default to black there instead of the white that suits saturated color themes.
    let effectiveTextColorStr = settings.textColor || config.textColor || (effectiveFullImage ? 'black' : 'white');
    let textColor = '#ffffff';
    
    if (effectiveTextColorStr.startsWith('#')) {
        textColor = effectiveTextColorStr;
    } else {
        const map: any = { black:'#000000', blue:'#1e3a8a', red:'#b91c1c', green:'#15803d', orange:'#ea580c', purple:'#7e22ce', gray:'#4b5563', yellow:'#facc15', pink:'#db2777', white:'#ffffff', navy:'#1e3a5f', forest:'#2f5d50', teal:'#0f766e', coral:'#e8556d', plum:'#6b2d5c', mustard:'#b8860b', graphite:'#334155', brown:'#7c4a2d' };
        textColor = map[effectiveTextColorStr] || '#ffffff';
    }

    if (icon && icon.id !== 'no-icon' && effectiveHasIcon) {
        let iSize: number, iX: number, iY: number;
        if (effectiveIconZone) {
            const zx = finalX + effectiveIconZone.xPct * w;
            const zy = finalY + effectiveIconZone.yPct * h;
            const zw = effectiveIconZone.wPct * w;
            const zh = effectiveIconZone.hPct * h;
            iSize = Math.min(Math.min(zw, zh) * (settings.iconScale || 1), w, h);
            iX = zx + (zw - iSize) / 2 + (settings.iconOffsetX || 0);
            iY = zy + (zh - iSize) / 2 + (settings.iconOffsetY || 0);
            // Zone placement + a manual nudge can otherwise push the icon past the
            // sticker's own edge — keep its whole bounding box on the sticker.
            iX = Math.min(Math.max(iX, finalX), finalX + w - iSize);
            iY = Math.min(Math.max(iY, finalY), finalY + h - iSize);
        } else {
            const iconT = clampIconTransform(settings.iconScale || 1, settings.iconOffsetX || 0, settings.iconOffsetY || 0, w, h, isCircle && !customShape);
            iSize = iconT.size;
            iX = finalX + iconT.x;
            iY = finalY + iconT.y;
        }

        if (icon.dataType === 'svg') {
            const info = getSvgInfo(icon.data);
            // Icons with CSS <style> blocks use complex clip-paths that simplified renderers
            // (Google Drive thumbnails, Mac Preview thumbs) can't handle — use <image> data URI
            // so the icon renders in its own isolated SVG document without clip-path issues.
            // Also use <image> for very large SVGs to avoid duplicating huge DOM nodes per sticker.
            const hasStyleBlock = /<style[\s>]/i.test(info.body);
            if (hasStyleBlock || icon.data.length > LARGE_SVG_THRESHOLD) {
                const dataUri = toSvgDataUri(icon.data);
                iconSvg = `<g id="interactive-icon" transform="translate(${iX}, ${iY})" style="pointer-events:all"><rect x="0" y="0" width="${iSize}" height="${iSize}" fill="transparent" /><image x="0" y="0" width="${iSize}" height="${iSize}" xlink:href="${dataUri}" href="${dataUri}" preserveAspectRatio="xMidYMid meet"/></g>`;
            } else {
                // Simple SVG (no CSS): inline with <g transform> for viewBox scaling.
                // No nested <svg> to avoid opaque-background issues in some renderers.
                const vbParts = info.viewBox.split(/[\s,]+/);
                const vbX = parseFloat(vbParts[0]) || 0;
                const vbY = parseFloat(vbParts[1]) || 0;
                const vbW = parseFloat(vbParts[2]) || 24;
                const vbH = parseFloat(vbParts[3]) || 24;
                const iconScaleFactor = Math.min(iSize / vbW, iSize / vbH);
                const iconTx = (iSize - vbW * iconScaleFactor) / 2 - vbX * iconScaleFactor;
                const iconTy = (iSize - vbH * iconScaleFactor) / 2 - vbY * iconScaleFactor;
                const iconClipId = `iconclip_${stickerSeed}`;
                defsInner += `<clipPath id="${iconClipId}"><rect x="0" y="0" width="${iSize}" height="${iSize}"/></clipPath>`;
                const iconContent = `<g fill="${textColor}">${scopeSvgIds(info.body, stickerSeed)}</g>`;
                iconSvg = `<g id="interactive-icon" transform="translate(${iX}, ${iY})" style="pointer-events:all"><g clip-path="url(#${iconClipId})"><rect x="0" y="0" width="${iSize}" height="${iSize}" fill="transparent" /><g transform="translate(${iconTx}, ${iconTy}) scale(${iconScaleFactor})">${iconContent}</g></g></g>`;
            }
        } else {
            // Raster image icon
            iconSvg = `<g id="interactive-icon" transform="translate(${iX}, ${iY})" style="pointer-events:all"><rect x="0" y="0" width="${iSize}" height="${iSize}" fill="transparent" /><image x="0" y="0" width="${iSize}" height="${iSize}" xlink:href="${icon.data}" href="${icon.data}" preserveAspectRatio="xMidYMid meet"/></g>`;
        }
    }

    // --- TEXT LAYER ---
    const lines = getLabelTextLines(config, settings);
    const activeFontFamily = settings.fontFamily || config.fontFamily || 'Arial';

    // Text zone (set by the admin on the template) overrides the default centered
    // placement: base font size and anchor point come from the zone rectangle, so
    // any name/class combination lands inside the area marked on the template art.
    // textOffsetX/Y and fontScale still apply on top, same as without a template.
    let fSize: number;
    let anchorX: number;
    let anchorY: number;
    let textAnchor: 'start' | 'middle' | 'end' = 'middle';
    const lineCount = lines.filter(Boolean).length || 1;
    const isBold = settings.isBold !== false;
    const isItalic = !!settings.isItalic;
    let widestLineWidth = 0;
    if (effectiveTextZone) {
        const zx = finalX + effectiveTextZone.xPct * w;
        const zy = finalY + effectiveTextZone.yPct * h;
        const zw = effectiveTextZone.wPct * w;
        const zh = effectiveTextZone.hPct * h;
        fSize = (zh / lineCount) * 0.75 * (settings.fontScale || 1);

        // Height-derived size can still overflow the zone sideways for long names —
        // measure the widest line and shrink to fit zw before laying out.
        lines.forEach(line => {
            if (!line) return;
            widestLineWidth = Math.max(widestLineWidth, measureTextWidth(line, fSize, activeFontFamily, isBold, isItalic));
        });
        if (widestLineWidth > zw) {
            fSize *= zw / widestLineWidth;
            widestLineWidth = zw;
        }

        const align = effectiveTextZone.align || 'center';
        textAnchor = align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle';
        anchorX = align === 'left' ? zx : align === 'right' ? zx + zw : zx + zw / 2;
        const blockHeight = lineCount * fSize * 1.1;
        anchorY = zy + zh / 2 - blockHeight / 2 + fSize * 0.85;
    } else {
        anchorX = finalX + w / 2;
        anchorY = finalY + h * 0.6;
        // The chosen size is the maximum: the text only shrinks when it would run past the sticker edge.
        const fit = fitLabelText(lines, baseLabelFontSize(w, h, settings), w, isCircle, activeFontFamily, isBold, isItalic);
        fSize = fit.fSize;
        widestLineWidth = fit.widest;
    }

    // A manual nudge ("Posun X/Y", drag or a stale saved value) can otherwise push
    // the whole text block past the sticker's own edge — clamp it back on.
    const blockHeight = lineCount * fSize * 1.1;
    const blockTop = anchorY - fSize * 0.85;
    let offsetY = settings.textOffsetY || 0;
    const minOffsetY = finalY - blockTop;
    const maxOffsetY = finalY + h - blockHeight - blockTop;
    if (minOffsetY <= maxOffsetY) {
        offsetY = Math.min(Math.max(offsetY, minOffsetY), maxOffsetY);
    }

    let tx = anchorX + (settings.textOffsetX || 0);
    if (textAnchor === 'start') {
        tx = Math.min(Math.max(tx, finalX), finalX + w - widestLineWidth);
    } else if (textAnchor === 'end') {
        tx = Math.min(Math.max(tx, finalX + widestLineWidth), finalX + w);
    } else {
        const half = widestLineWidth / 2;
        tx = Math.min(Math.max(tx, finalX + half), finalX + w - half);
    }

    let textRows = '';
    lines.forEach((line, i) => {
        if (!line) return;
        const ty = anchorY + (i * fSize * 1.1) + offsetY;
        textRows += `<text x="${tx}" y="${ty}">${escapeXml(line)}</text>`;
    });

    const textSvg = !effectiveHasText ? '' : `<g id="interactive-text" style="pointer-events:all" font-family="${activeFontFamily}, Arial, sans-serif" font-size="${fSize}" font-weight="${settings.isBold !== false ? 'bold' : 'normal'}" font-style="${settings.isItalic ? 'italic' : 'normal'}" fill="${textColor}" text-anchor="${textAnchor}">${textRows}</g>`;
    
    const standardCutLine = !includeCutLineMarks ? '' : effectiveFullImage
        ? `<g id="CutContour" style="pointer-events:none">${templateCutOutline}</g>`
        : `<g id="CutContour" fill="none" stroke="#FF00FF" stroke-width="0.2" style="pointer-events:none">${shapePath}</g>`;

    return `<g id="sticker_${stickerSeed}">
        <defs>${defsInner}</defs>
        <g clip-path="url(#${clipId})">
            ${backgroundLayer}
            ${textSvg}
            ${iconSvg}
        </g>
        ${standardCutLine}
    </g>`;
}


const MAX_ICON_PX = 300;
const MAX_BG_PX = 800;

// Deduplicate background/template images (non-zero x/y) via <symbol>+<use>.
// Icons (x="0" y="0", positioned by parent <g transform>) are skipped —
// they're resized to 600px and kept as direct <image> to avoid Corel Draw
// black-square rendering bug with <use>→<symbol>→<image>. The footer logo
// (class="footer-logo") is kept as a direct <image> for the same reason.
//
// The symbol's viewBox must have the SAME proportions as the box it is placed into. A fixed square
// viewBox (as this once used) is fitted into a non-square box with "meet": a 60×30 template ended up
// as a 30×30 square in the middle of its sticker, and a wide logo shrank to a fraction of its size.
// So one symbol is shared per image + placement mode + box size (i.e. by all identical stickers).
function deduplicateBgImages(svg: string): string {
    const seen = new Map<string, string>();
    let counter = 0;
    const symbols: string[] = [];

    const processed = svg.replace(
        /<image([^>]*?) xlink:href="(data:[^"]{200,})"([^>]*?)\/>/g,
        (match, before, dataUri, after) => {
            const allAttrs = (before + ' ' + after).trim();
            if (/\bclass="footer-logo"/.test(allAttrs)) return match; // footer logo — keep as is
            const x = (allAttrs.match(/\bx="([^"]*)"/) || [])[1] || '0';
            const y = (allAttrs.match(/\by="([^"]*)"/) || [])[1] || '0';
            if (x === '0' && y === '0') return match; // icon — skip

            const width  = (allAttrs.match(/\bwidth="([^"]*)"/)  || [])[1] || '100';
            const height = (allAttrs.match(/\bheight="([^"]*)"/) || [])[1] || '100';
            if (!(parseFloat(width) > 0) || !(parseFloat(height) > 0)) return match; // no usable box — leave the image alone
            const pa     = (allAttrs.match(/preserveAspectRatio="([^"]*)"/) || [])[1] || 'xMidYMid meet';
            const filter = (allAttrs.match(/filter="([^"]*)"/) || [])[1];
            const filterAttr = filter ? ` filter="${filter}"` : '';

            const key = `${width}x${height}|${pa}|${dataUri}`;
            if (!seen.has(key)) {
                const id = `__sym_${counter++}`;
                seen.set(key, id);
                symbols.push(`<symbol id="${id}" viewBox="0 0 ${width} ${height}"><image xlink:href="${dataUri}" href="${dataUri}" x="0" y="0" width="${width}" height="${height}" preserveAspectRatio="${pa}"/></symbol>`);
            }
            const id = seen.get(key)!;
            return `<use xlink:href="#${id}" href="#${id}" x="${x}" y="${y}" width="${width}" height="${height}"${filterAttr}/>`;
        }
    );

    if (symbols.length === 0) return svg;
    const defsBlock = `<defs id="__dedup">${symbols.join('')}</defs>`;
    return processed.replace(/<svg([^>]*)>/, `<svg$1>${defsBlock}`);
}

async function resizeRasterDataUri(dataUri: string, maxPx: number): Promise<string> {
    if (!dataUri || dataUri.startsWith('data:image/svg+xml')) return dataUri;
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            if (img.width <= maxPx && img.height <= maxPx) { resolve(dataUri); return; }
            const scale = maxPx / Math.max(img.width, img.height);
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
            const resized = canvas.toDataURL('image/png');
            // If canvas re-encoding made the file larger, keep the original
            resolve(resized.length < dataUri.length ? resized : dataUri);
        };
        img.onerror = () => resolve(dataUri);
        img.src = dataUri;
    });
}

async function resizeImagesInConfig(config: LabelConfig): Promise<LabelConfig> {
    const cache = new Map<string, string>();
    const resize = async (data: string, maxPx: number) => {
        const key = `${maxPx}:${data}`;
        if (!cache.has(key)) cache.set(key, await resizeRasterDataUri(data, maxPx));
        return cache.get(key)!;
    };

    const globalIcon = config.icon?.dataType === 'url'
        ? { ...config.icon, data: await resize(config.icon.data, MAX_ICON_PX) }
        : config.icon;

    const newVariantSettings: typeof config.variantSettings = {};
    for (const [key, s] of Object.entries(config.variantSettings)) {
        const customIcon = s.customIcon?.dataType === 'url'
            ? { ...s.customIcon, data: await resize(s.customIcon.data, MAX_ICON_PX) }
            : s.customIcon;
        const fullImageData = s.fullImageData
            ? await resize(s.fullImageData, MAX_BG_PX)
            : s.fullImageData;
        newVariantSettings[key] = { ...s, customIcon, fullImageData };
    }

    return { ...config, icon: globalIcon, variantSettings: newVariantSettings };
}

export async function createStickerFile(
    config: LabelConfig,
    quantity: number,
    token: string,
    customBackgrounds: CustomBackground[] = [],
    customShapes: CustomShape[] = [],
    appLogo?: string,
    customTemplates: CustomTemplate[] = [],
    sheetWidth: number = A4_WIDTH,
    sheetHeight: number = A4_HEIGHT,
    bleedMm: number = 1,
    includeCutLines: boolean = true
): Promise<{ blob: Blob; fileName: string; token: string; content: string }> {
    await ensureFontsLoaded(config);
    const resizedConfig = await resizeImagesInConfig(config);

    const resizedBackgrounds = await Promise.all(
        customBackgrounds.map(async bg => ({ ...bg, data: await resizeRasterDataUri(bg.data, MAX_BG_PX) }))
    );
    const resizedTemplates = await Promise.all(
        customTemplates.map(async t => ({ ...t, data: await resizeRasterDataUri(t.data, MAX_BG_PX) }))
    );
    const pages = calculateDynamicLayout(resizedConfig, sheetWidth, sheetHeight);
    const totalHeight = sheetHeight * pages.length;
    const pagesPerSet = pages.length;

    const LOGO_H = 7;
    const LOGO_W = 32;
    const FOOTER_Y = sheetHeight - LOGO_H - 1;
    const TEXT_Y = FOOTER_Y + LOGO_H - 1;

    let content = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${sheetWidth} ${totalHeight}" width="${sheetWidth}mm" height="${totalHeight}mm">\n`;

    for (let i = 0; i < pages.length; i++) {
        const pageY = i * sheetHeight;
        const pageLabel = `${i + 1}/${pagesPerSet}`;
        content += `<g id="page-${i+1}" transform="translate(0, ${pageY})">\n`;
        content += pages[i].map(item => generateLabelSvg(resizedConfig, item.variant, item.x, item.y, item.w, item.h, true, resizedBackgrounds, customShapes, `p${i}s${Math.random().toString(36).substr(2, 5)}`, false, resizedTemplates, bleedMm, includeCutLines)).join('\n');

        // Footer: logo
        if (appLogo) {
            content += `\n<image class="footer-logo" x="${MARGIN}" y="${FOOTER_Y}" width="${LOGO_W}" height="${LOGO_H}" xlink:href="${appLogo}" href="${appLogo}" preserveAspectRatio="xMinYMid meet"/>`;
        }

        // Footer: barcode + QR + ID text
        const barcodeHeight = 7;
        const barcodeWidth = getBarcodeWidth(token);
        const barcodeX = sheetWidth - 10 - barcodeWidth;
        content += `\n${generateCode128Barcode(token, barcodeX, TEXT_Y - barcodeHeight, barcodeHeight)}`;

        const qrSize = 7;
        const qrX = barcodeX - qrSize - 3;
        const qrSvg = await generateQrCodeSvg(token, qrX, TEXT_Y - qrSize, qrSize);
        content += `\n${qrSvg}`;

        content += `\n<text x="${MARGIN + 35}" y="${TEXT_Y}" text-anchor="start" font-family="Arial, sans-serif" font-size="2.5" fill="#000000" font-weight="bold">ID: ${escapeXml(token)} ${escapeXml(pageLabel)}</text>`;

        content += `\n</g>\n`;
    }
    content += `</svg>`;
    content = deduplicateBgImages(content);

    return { blob: new Blob([content], { type: 'image/svg+xml' }), fileName: `samolepky_${token}.svg`, token, content };
}

const BARCODE_MODULE_MM = 0.25;   // width of the thinnest bar

/** Width in mm of the barcode for `text` (without quiet zones), 0 if it cannot be encoded. */
export function getBarcodeWidth(text: string): number {
    return code128ModuleCount(text) * BARCODE_MODULE_MM;
}

/** A real Code 128 barcode (see code128.ts) that a scanner can read back as `text`. Empty if `text` cannot be encoded. */
export function generateCode128Barcode(text: string, x: number, y: number, height: number): string {
    const widths = code128Widths(text);
    if (!widths) return '';
    const mm = (n: number) => Math.round(n * 1000) / 1000;
    let res = `<g>`;
    let currX = x;
    widths.forEach((w, i) => {
        // Elements alternate bar, space, bar … — each bar is one rectangle however many modules wide.
        if (i % 2 === 0) res += `<rect x="${mm(currX)}" y="${y}" width="${mm(w * BARCODE_MODULE_MM)}" height="${height}" fill="black" />`;
        currX += w * BARCODE_MODULE_MM;
    });
    res += `</g>`;
    return res;
}

export async function generateQrCodeSvg(text: string, x: number, y: number, size: number): Promise<string> {
    if (!text) return '';
    try {
        const svgString = await QRCode.toString(text, { type: 'svg', margin: 0 });
        const { body, viewBox } = getSvgInfo(svgString);
        
        const vbParts = viewBox.split(' ');
        const vbW = parseFloat(vbParts[2]) || 24;
        const scale = size / vbW;
        
        return `<g transform="translate(${x}, ${y}) scale(${scale})">${body}</g>`;
    } catch (e) { return ''; }
}

export function calculateDynamicLayout(config: LabelConfig, sheetWidth: number = A4_WIDTH, sheetHeight: number = A4_HEIGHT): LayoutItem[][] {
    const pages: LayoutItem[][] = [];
    let currentPage: LayoutItem[] = [];
    const marginTop = config.customMarginTop !== undefined ? config.customMarginTop : MARGIN;
    const marginLeft = config.customMarginLeft !== undefined ? config.customMarginLeft : MARGIN;
    const contentWidth = sheetWidth - marginLeft * 2;
    const contentHeight = sheetHeight - marginTop * 2;
    let curX = marginLeft;
    let curY = marginTop;
    let rowMaxHeight = 0;
    const variants = Object.keys(config.variantSettings);

    for (const variant of variants) {
        const settings = config.variantSettings[variant];
        if (settings.visible === false) continue;
        const width = settings.customWidth || 26;
        const height = settings.customHeight || 20;
        const gapX = settings.customGapX !== undefined ? settings.customGapX : GAP;
        const gapY = settings.customGapY !== undefined ? settings.customGapY : GAP;
        let count = settings.customCount !== undefined ? settings.customCount : 0;
        if (count <= 0) continue;

        for (let i = 0; i < count; i++) {
            if (curX + width > marginLeft + contentWidth + 0.1) {
                curX = marginLeft;
                curY += rowMaxHeight + gapY;
                rowMaxHeight = 0;
            }
            if (curY + height > marginTop + contentHeight + 0.1) {
                pages.push(currentPage);
                currentPage = [];
                curX = marginLeft;
                curY = marginTop;
                rowMaxHeight = 0;
            }
            currentPage.push({ x: curX, y: curY, w: width, h: height, variant: variant });
            curX += width + gapX;
            rowMaxHeight = Math.max(rowMaxHeight, height);
        }
    }
    if (currentPage.length > 0) pages.push(currentPage);
    return pages.length > 0 ? pages : [[]];
}

// Bleed extends each sticker's background past its own cut line, but the layout only
// reserves `gap`/`margin` mm of space around it. If bleed exceeds half the gap between
// stickers (or the outer margin), neighboring stickers' bleed areas overlap each other
// or spill past the sheet edge — the cut sticker size stays correct, but the printed
// sheet shows wrong-looking boundaries/margins around it. Clamp bleed to whatever the
// tightest configured gap/margin actually allows.
export function getMaxSafeBleed(config: LabelConfig): number {
    const marginTop = config.customMarginTop !== undefined ? config.customMarginTop : MARGIN;
    const marginLeft = config.customMarginLeft !== undefined ? config.customMarginLeft : MARGIN;
    let maxBleed = Math.min(marginTop, marginLeft);
    for (const settings of Object.values(config.variantSettings)) {
        if (settings.visible === false) continue;
        const gapX = settings.customGapX !== undefined ? settings.customGapX : GAP;
        const gapY = settings.customGapY !== undefined ? settings.customGapY : GAP;
        maxBleed = Math.min(maxBleed, gapX / 2, gapY / 2);
    }
    return Math.max(0, maxBleed);
}
